/**
 * Keeps raw runtime errors out of user-visible copy.
 *
 * Every catch block that renders an error to the user used to do:
 *
 *   err instanceof Error ? err.message : 'Something went wrong'
 *
 * That is correct for OUR curated copy and for the server's error messages,
 * and wrong for everything else: a dead network throws
 * `TypeError: Failed to fetch`, a truncated body throws
 * `SyntaxError: Unexpected token <`, and a wrong-shape 200 throws
 * `TypeError: integrations.map is not a function`. All three reached real
 * users as their error message (error-path harness findings EP-005, EP-006,
 * EP-025, EP-067, EP-088).
 *
 * This is deliberately a DENYLIST. Curated copy still passes through
 * verbatim, because the server's own message ("Your plan limit has been
 * reached") is usually the most useful thing we can say. Only recognisably
 * internal text is replaced.
 */
/** Shown when the request never reached the server (offline, DNS, blocked). */
export const NETWORK_FAILURE_COPY = "Couldn't reach the server. Check your connection and try again.";
/** Shown when a response arrived but could not be parsed. */
export const UNREADABLE_RESPONSE_COPY = 'The server sent a response we could not read. Try again in a moment.';
/**
 * Text that means something to an engineer and nothing to a user.
 *
 * Kept in sync with `isDeveloperSpeak` in the error-path harness
 * (`packages/extension/e2e/journey-harness/journeys/error-paths/drivers.ts`),
 * which is what asserts this never renders. This pattern must stay a superset
 * of the harness one.
 */
const DEVELOPER_SPEAK_PROSE = new RegExp([
    'failed to fetch',
    'unexpected token',
    'unexpected end of (json|input)',
    'networkerror',
    'network request failed',
    'load failed',
    'typeerror',
    'syntaxerror',
    'is not a function',
    'is not an object',
    'cannot read propert',
    '(undefined|null) is not',
    '\\[object object\\]',
    'status code \\d',
    // Zod validation text, leaked through apiProxy's `(<field>: <message>)`
    // suffix (2026-08-13: a user was shown "figmaImageBase64: String must
    // contain at most 15000000 character(s)" on a failed compare, five times).
    //
    // Anchored on Zod's own leading TYPE word rather than on the parenthetical
    // shape. "(word: text)" would swallow curated copy like "(Figma: not
    // connected)", and a bare "must be greater than" would swallow a
    // hand-written "Width must be greater than 320".
    '\\b(string|number|array|date) must (contain|be)\\b',
    'expected \\w+, received \\w+',
    // Our OWN proxy's words, leaked to a user (2026-08-13: two compare runs
    // showed "Invalid API response (502)" in the error banner). apiProxy.ts
    // throws this when a response body is not JSON, which is what an EDGE 502
    // looks like — our Hono app always answers JSON, so this string never
    // describes something the user or the product can act on. The status code
    // is already on the telemetry event, which is where it belongs.
    //
    // Anchored on the whole phrase, not on "Invalid": the curated sibling
    // "Invalid request. Please check your input and try again." must still
    // reach the user, and a control pins that.
    'invalid api response',
    // Extension-messaging deaths. chrome.runtime.sendMessage surfaces these
    // when the MV3 service worker is gone (mid-update, evicted) or the port
    // closed before it answered; the bulk-create hooks reject with the raw
    // sentence and toasted it verbatim (found 2026-09-09 by the
    // useCompareCreateIssues undo fills). Sibling list for the RETRY side:
    // utils/transientExtensionErrors.ts; this one decides only what a user
    // is allowed to read, so the four Chrome phrasings are spelled out here.
    'could not establish connection',
    'receiving end does not exist',
    'message (port|channel) closed',
    'extension context invalidated',
].join('|'), 'i');
/**
 * System error codes are literal uppercase tokens, so this half is
 * deliberately CASE-SENSITIVE and word-bounded. A case-insensitive `ECONN`
 * matches "R-econn-ecting", which flagged our own curated 429 copy
 * ("Reconnecting Figma won't help") as developer speak and replaced it with
 * generic network copy. Caught by the 429 test on first wiring.
 */
const DEVELOPER_SPEAK_CODES = /\b(ECONN[A-Z]*|ERR_[A-Z_]{3,}|EAI_[A-Z]+|ENOTFOUND|ETIMEDOUT)\b/;
/**
 * Our OWN placeholder strings, not real server copy.
 *
 * `client.ts` falls back to these when a response carries no message, so they
 * reach the catch looking like a legitimate server message and then get
 * rendered verbatim. "API request failed" tells a user nothing and leaks the
 * word "API"; the caller's fallback ("Couldn't load comments.") names what
 * actually failed. Exact matches only, so a real short server message like
 * "Rate limited" still passes through. The status-suffixed form, "Request failed (400)", is the same
 * placeholder: the review-app and a11y fetch wrappers mint it when a 400 body carries no message (a schema
 * refusal), and it reached the team review file popover verbatim on 2026-10-25.
 */
const GENERIC_PLACEHOLDER = /^((api )?request failed( \(\d{3}\))?|something went wrong\.?( please try again\.?)?|failed|error|unknown error)\.?$/i;
const NETWORK_FAILURE_PROSE = /failed to fetch|networkerror|network request failed|load failed/i;
// The ERR_* alternatives are PREFIXES, like the ECONN one beside them.
// They ended in `\b` until 2026-09-01, which made three of the four dead:
// Chrome emits ERR_INTERNET_DISCONNECTED and ERR_CONNECTION_REFUSED, never
// the bare prefix, and `_` is a word character so no boundary exists there.
// Such a message reached isDeveloperSpeak (whose ERR_[A-Z_]{3,} DOES span
// underscores), got replaced, and then fell through to the generic fallback
// instead of the network copy that names the actual problem.
const NETWORK_FAILURE_CODES = /\b(ECONN[A-Z]*|ENOTFOUND|ETIMEDOUT|EAI_[A-Z]+|ERR_(NETWORK|INTERNET|CONNECTION|NAME_NOT_RESOLVED)[A-Z_]*)\b/;
const UNREADABLE_RESPONSE = /unexpected token|unexpected end of|syntaxerror|json/i;
/**
 * True when this throw means the request never completed over the network:
 * offline, DNS, a blocked or dropped connection.
 *
 * Exported because two things must agree about it and used to be able to
 * drift: the copy the user reads, and handleSaveFailure's decision to persist
 * their capture locally instead of leaving it in React state. Deliberately
 * NARROW -- a timeout is excluded, because a timed-out POST may have committed
 * server-side, and an untyped server error is excluded, because rerouting
 * every statusless failure would send ordinary server errors to local storage.
 */
export function isNetworkFailure(err) {
    if (!(err instanceof Error))
        return false;
    const message = err.message.trim();
    if (!message)
        return false;
    return NETWORK_FAILURE_PROSE.test(message) || NETWORK_FAILURE_CODES.test(message);
}
/** True when `text` would leak internal detail if rendered to a user. */
export function isDeveloperSpeak(text) {
    return DEVELOPER_SPEAK_PROSE.test(text) || DEVELOPER_SPEAK_CODES.test(text);
}
/**
 * Convert an unknown thrown value into copy that is safe to show a user.
 *
 * Pass a bare string for the common case where only the fallback differs.
 */
export function toUserFacingMessage(err, copy) {
    const resolved = typeof copy === 'string' ? { fallback: copy } : copy;
    const message = err instanceof Error ? err.message.trim() : '';
    if (!message)
        return resolved.fallback;
    if (GENERIC_PLACEHOLDER.test(message))
        return resolved.fallback;
    if (!isDeveloperSpeak(message))
        return message;
    if (NETWORK_FAILURE_PROSE.test(message) || NETWORK_FAILURE_CODES.test(message)) {
        return resolved.network ?? NETWORK_FAILURE_COPY;
    }
    if (UNREADABLE_RESPONSE.test(message))
        return resolved.unreadable ?? UNREADABLE_RESPONSE_COPY;
    return resolved.fallback;
}
/**
 * Copy for a failed issue create (bulk or single), shared by the extension's
 * four workflow hooks (a11y, token audit, compare, auto review) AND Team
 * Review's auto-review and accessibility hooks, so the two surfaces render ONE
 * sentence for the same failure. Team Review rendered the raw runtime message
 * until 2026-10-07: a dead network showed "Failed to fetch" in the panel while
 * the extension's toast for the identical fault read the `network` line below
 * (frame `team-review-lost-response-retryable.png` beside
 * `extension-lost-response-retryable.png`, docs/test-reports/2026-10-07-auto-review-repairs).
 * Server envelope messages still pass through toUserFacingMessage verbatim.
 */
export const BULK_CREATE_FAILURE_COPY = {
    fallback: "Couldn't create issues. Try again.",
    network: "Couldn't create issues. Check your connection and try again.",
    unreadable: "Couldn't create issues. Try again in a moment.",
};
//# sourceMappingURL=userFacingError.js.map